<template>
  <NuxtLayout>
    <div v-if="isSuccessParticipant && isSuccess" ref="list" class="h-full w-full grid gap-4 participant-tmpl-wrapper">
      <div
        class="pt-1 md:px-2 lg:px-3 overflow-auto hidden lg:flex flex-col list-wrapper"
      >
        <sgdf-icon-button
          ref="list_toggle"
          name="angles-left"
          class="self-end"
          @click="toggleList"
        />
        <sgdf-participant-list
          :participants="data"
          :skeleton="!isSuccess"
          :current-id.camel="participantId"
          mode="none"
          :sort-field.camel="route.query.sortField"
          :sort-dir.camel="route.query.sortDir"
          @sgdf-participant:navigate="onParticipantClick"
          @sgdf-participants:sort="onSortUpdate"
        />
      </div>
      <div class="pt-2 px-0 overflow-hidden relative main-part">
        <div
          class="inter-participant-nav @container flex flex-row-reverse justify-between"
          style="background: #fff"
        >
          <div
            v-if="isSuccessParticipant"
            class="flex space-x-2 flex-grow justify-center"
          >
            <sgdf-icon-button
              .disabled="!hasPrevious"
              name="arrow-left"
              class="hide-on-large"
              @click="onPrev"
            />
            <sgdf-icon-button
              .disabled="!hasNext"
              name="arrow-right"
              class="hide-on-large"
              @click="onNext"
            />
            <sgdf-button
              .disabled="!hasPrevious"
              class="display-on-large"
              @click="onPrev"
            >
              Précédent
            </sgdf-button>
            <sgdf-button
              .disabled="!hasNext"
              class="display-on-large"
              @click="onNext"
            >
              Suivant
            </sgdf-button>
          </div>
          <sgdf-icon-button
            name="list"
            class="block xl:hidden"
            @click="onBackToList"
          />
        </div>
        <div
          v-if="isSuccessParticipant"
          ref="simpleHeader"
          class="hide thesimple"
        >
          <sgdf-participant-view
            class="simple"
            simple="true"
            :participant="participant"
            :state="route.params.view"
            :comment-enabled.camel="commentEnabled"
            :opinion-enabled.camel="commentEnabled"
            @sgdf-participant:show-comment="onShowComment"
            @sgdf-participant:show-detail="onShowDetail"
            @sgdf-participant:show-validation="onShowValidation"
            ><img
              slot="img-fallback"
              src="~/assets/images/scoot.png"
              alt=""
              class="object-fill"
            />
          </sgdf-participant-view>
        </div>
        <div ref="scrollRoot" class="h-full overflow-auto pb-10 l:pb-2">
          <div v-if="isSuccessParticipant" class="w-full px-4">
            <sgdf-participant-view
              ref="bigHeader"
              class="big-view"
              :participant="participant"
              :state="route.params.view"
              :comment-enabled.camel="commentEnabled"
              :opinion-enabled.camel="commentEnabled"
              @sgdf-participant:show-comment="onShowComment"
              @sgdf-participant:show-detail="onShowDetail"
              @sgdf-participant:show-validation="onShowValidation"
              ><img
                slot="img-fallback"
                src="~/assets/images/scoot.png"
                alt=""
                class="object-fill"
              />
            </sgdf-participant-view>
          </div>
          <slot></slot>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import "@sgdf/ui-library/sgdf-participant-list.js";
import { useSortParticipants } from "~/composables/sort-participant";
import { ref, onMounted, onUpdated, computed, watch, nextTick } from "vue";
import { useSortStore } from "~/composables/sort-store";
import { useHeaderVisibilityToggle } from "~/composables/header-visibility";

const sortStore = useSortStore();

const bigHeader = ref();
const simpleHeader = ref();
const scrollRoot = ref();
const list = ref();
const list_toggle = ref();

const {
  data: participant,
  isSuccess: isSuccessParticipant,
  status,
} = useCurrentStagiaire();

const { data: interns, isSuccess } = useInterns();

const { data, route } = useSortParticipants(interns);

const meetingId = useMeetingId();
const participantId = useStagiaireId();

const hasModule = useModule();
// L'onglet « Observations » et l'onglet « Avis » (rédaction de l'avis de stage)
// suivent le même module, comme la relecture des avis côté paramétrage.
const commentEnabled = computed(() =>
  hasModule.value("participant_comment", meetingId.value),
);

const viewParam = computed(() =>
  Array.isArray(route.params.view) ? route.params.view[0] : route.params.view,
);
const safeQuery = computed(() =>
  Object.fromEntries(
    Object.entries(route.query).map(([k, v]) => [
      k,
      Array.isArray(v) ? (v[0] ?? "") : (v ?? ""),
    ]),
  ),
);

const onSortUpdate = async (
  e: CustomEvent<{ sortField?: string; sortDir?: string }>,
) => {
  const { sortDir = "asc", sortField = "first_name" } = e.detail;
  sortStore.setSortField(sortField);
  sortStore.setSortDir(sortDir);
  await navigateTo(
    RouteUtils.getMeetingParticipant(
      meetingId.value!,
      participantId.value!,
      viewParam.value,
      {
        ...safeQuery.value,
        sortDir,
        sortField,
      },
    ),
  );
};

const toggleList = () => {
  list.value.classList.toggle("nolist");
  if (list.value.classList.contains("nolist")) {
    list_toggle.value.setAttribute("name", "angles-right");
  } else {
    list_toggle.value.setAttribute("name", "angles-left");
  }
};

watchEffect(() => {
  if (status.value === "error") {
    navigateTo(RouteUtils.getMeetingParticipants(meetingId.value!));
  }
});

const currentIndex = computed(() =>
  data.value.findIndex((p) => p.id === participantId.value),
);
const hasNext = computed(() => currentIndex.value < data.value.length - 1);
const hasPrevious = computed(() => currentIndex.value > 0);

const navigateRelative = async (delta: number) => {
  if (!participant.value) return;
  const nextIndex = currentIndex.value + delta;
  if (nextIndex < 0 || nextIndex >= data.value.length) return;
  await navigateTo(
    RouteUtils.getMeetingParticipant(
      meetingId.value!,
      data.value[nextIndex].id,
      viewParam.value,
      safeQuery.value,
    ),
  );
};
const onNext = async () => navigateRelative(1);
const onPrev = async () => navigateRelative(-1);

const onParticipantClick = async (
  e: CustomEvent<{
    participantId: string;
    action: "check-in" | "validation" | "interview" | "link";
  }>,
) => {
  await navigateTo(
    RouteUtils.getMeetingParticipant(
      meetingId.value!,
      e.detail.participantId,
      viewParam.value,
      safeQuery.value,
    ),
  );
};

const onShowComment = async (e: CustomEvent<{ id: string }>) => {
  if (!meetingId.value) return;
  await navigateTo(
    RouteUtils.getMeetingParticipant(
      meetingId.value!,
      e.detail.id,
      "comment",
      safeQuery.value,
    ),
  );
};

const onShowDetail = async (e: CustomEvent<{ id: string }>) => {
  if (!meetingId.value) return;
  await navigateTo(
    RouteUtils.getMeetingParticipant(
      meetingId.value!,
      e.detail.id,
      "detail",
      safeQuery.value,
    ),
  );
};

const onShowValidation = async (e: CustomEvent<{ id: string }>) => {
  if (!meetingId.value) return;
  await navigateTo(
    RouteUtils.getMeetingParticipant(
      meetingId.value!,
      e.detail.id,
      "validation",
      safeQuery.value,
    ),
  );
};

const onBackToList = async (_: Event) => {
  if (!meetingId.value) return;
  await navigateTo(RouteUtils.getMeetingParticipants(meetingId.value!, {}));
};

const headerToggle = useHeaderVisibilityToggle(scrollRoot, bigHeader, simpleHeader, 0.6);

onMounted(() => headerToggle.init());
onUpdated(() => {
  headerToggle.observe();
  headerToggle.updateVisibility();
});

// Ensure observer is ready when participant data loads
watch(
  [isSuccessParticipant, () => bigHeader.value],
  async ([ready, header]) => {
    if (ready && header) {
      await nextTick();
      headerToggle.observe();
      headerToggle.updateVisibility();
    }
  },
  { immediate: true },
);
</script>
<style>
.thesimple {
  position: absolute;
  top: 2.6em;
  left: 1rem;
  right: 1rem;
  z-index: 10;
  overflow: hidden;

  &.hide {
    display: none;
  }
}

.inter-participant-nav {
  padding-inline-start: 1em;
  padding-inline-end: 1em;

  .display-on-large {
    display: block;
  }
  .hide-on-large {
    display: none;
  }
}

.participant-tmpl-wrapper {
  grid-template-columns: 1fr 3fr;

  sgdf-icon-button {
    font-size: 1.3em;
  }

  .list-wrapper {
    border-right: 3px solid var(--sgdf-color-neutral-200);
  }

  &.nolist {
    grid-template-columns: auto 1fr;

    .list-wrapper {
      padding: 0;
      border-right: none;
    }

    sgdf-participant-list {
      display: none;
    }

    sgdf-icon-button {
      align-self: center;
    }
    /*
    sgdf-icon-button::part(base) {
      color: white;
    }
    */
  }
}

@media (max-width: 1023px) {
  .participant-tmpl-wrapper {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1023px) {
  .main-part {
    grid-column-start: 1;
    grid-column-end: 3;
  }
}

@media (min-width: 1024px) {
  .participant-tmpl-wrapper {
    grid-template-columns: 2fr 5fr;
  }
}

@media (max-width: 800px) {
  .thesimple {
    top: 0;
    left: 0;
    right: 0;
    padding: 0.5rem 1rem 0 1rem;
  }

  .inter-participant-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 30;
    justify-content: start;
    gap: 2em;
    padding-block-start: 0.3em;
    padding-block-end: 0.5em;
  }
}
@media (max-width: 450px) {
  .inter-participant-nav {
    gap: 2em;
    padding-inline-end: 3em;

    .display-on-large {
      display: none;
    }
    .hide-on-large {
      display: block;
    }
  }
}
</style>
